import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Button, Flex, Grid, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {useProducts} from '@salesforce/commerce-sdk-react'
import UniversalFeedbackProducts from './universal-feedback-products'

const UniversalFeedbackRecommended = ({products = [], isSubscription = false}) => {
    const styles = useMultiStyleConfig('Modal')
    const intl = useIntl()
    const [visibleProducts, setVisibleProducts] = useState(2)
    const [visibleSubscriptions, setVisibleSubscriptions] = useState(isSubscription ? 4 : 2)
    const [preventFocus, setPreventFocus] = useState(false)

    const p = products.join(',')

    const {data: recommendedProducts, isLoading} = useProducts({
        parameters: {
            ids: p,
            allImages: true,
            perPricebook: true,
            expand: [
                'availability',
                'links',
                'promotions',
                'options',
                'images',
                'prices',
                'variations',
            ],
        },
    })

    useEffect(() => {
        if (preventFocus) {
            setPreventFocus(false)
        }
    }, [preventFocus])

    const inStockProducts = recommendedProducts?.data?.filter(
        (product) => product?.inventory?.stockLevel > 0,
    )

    const subscriptionProducts = recommendedProducts?.data?.filter(
        (product) => product?.c_isSubscription === 'true',
    )

    const handleDiscoverMoreProducts = () => {
        setVisibleProducts((prev) => Math.min(prev + 4, inStockProducts.length))
    }

    const handleDiscoverMoreSubscriptions = () => {
        setVisibleSubscriptions((prev) => Math.min(prev + 4, subscriptionProducts.length))
    }

    if ((!inStockProducts?.length && !subscriptionProducts?.length) || isLoading) {
        return null
    }

    return (
        <Stack {...styles.scrollContainer} {...styles.modalRecommendedFull}>
            <Flex sx={styles.productGrid}>
                {visibleProducts && inStockProducts?.length > 0 && (
                    <>
                        <Text {...styles.modalRecommendedTitle}>
                            {intl.formatMessage({
                                defaultMessage: 'Add accessories',
                                id: 'add_to_cart_modal.label.recommendeed_products',
                            })}
                        </Text>
                        <Grid {...styles.recommendedGrid}>
                            {inStockProducts
                                .slice(0, visibleProducts)
                                .filter((product) => {
                                    return product?.variants?.length > 0
                                })
                                .map((product, i) => {
                                    return <UniversalFeedbackProducts key={i} product={product} />
                                })}
                        </Grid>
                        {visibleProducts < inStockProducts.length && (
                            <Button
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleDiscoverMoreProducts}
                                {...styles.modalRecommendedFull}
                                variant="text-button"
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'Discover More',
                                    id: 'add_to_cart_modal.link.discover_more',
                                })}
                            </Button>
                        )}
                    </>
                )}
                {visibleSubscriptions && subscriptionProducts?.length > 0 && (
                    <>
                        {!isSubscription && ( // only show the title in the normal add to cart modal
                            <Text {...styles.modalRecommendedTitle}>
                                {intl.formatMessage({
                                    defaultMessage: 'Buy in subscription and save ',
                                    id: 'add_to_cart_modal.label.recommendeed_subscriptions',
                                })}
                            </Text>
                        )}
                        <Grid {...styles.recommendedGrid}>
                            {subscriptionProducts
                                .slice(0, visibleSubscriptions)
                                .filter((product) => {
                                    return product?.c_isSubscription === 'true'
                                })
                                .map((product, i) => {
                                    return (
                                        <UniversalFeedbackProducts
                                            key={i}
                                            product={product}
                                            isPurchaseBuy={isSubscription}
                                        />
                                    )
                                })}
                        </Grid>
                        {visibleSubscriptions < subscriptionProducts.length && (
                            <Button
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleDiscoverMoreSubscriptions}
                                {...styles.modalRecommendedFull}
                                variant="text-button"
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'Discover More',
                                    id: 'add_to_cart_modal.link.discover_more',
                                })}
                            </Button>
                        )}
                    </>
                )}
            </Flex>
        </Stack>
    )
}

UniversalFeedbackRecommended.propTypes = {
    products: PropTypes.array,
    isSubscription: PropTypes.bool,
}

export default UniversalFeedbackRecommended
